import type {
  CreateGenerationQueueItemRequest,
  EntryOrFreeText,
  GenerationQueueItemResponse,
} from '@malaberg/shared';
import { ActionIcon, Alert, Badge, Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconPlayerPlay,
  IconRefresh,
  IconRotateClockwise,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useGenerationQueue } from '../../hooks/use-generation-queue';
import { useRunStreamStore } from '../../state/run-stream-store';
import { generationModelLabel } from '../../utils/generation-models';
import { GenerationQueueItemEditor } from './GenerationQueueItemEditor';
import { SectionHeading } from './SectionHeading';

type QueueStatus = GenerationQueueItemResponse['status'];
type EditorState = {
  item: GenerationQueueItemResponse;
  mode: 'edit' | 'requeue';
};

const ACTIONABLE_STATUSES: QueueStatus[] = ['queued', 'running', 'retrying', 'failed'];

export function GenerationQueuePanel() {
  const {
    state,
    items,
    loading,
    mutating,
    error,
    refresh,
    edit,
    reorder,
    cancel,
    requeue,
    resume,
    cancelRemaining,
  } = useGenerationQueue();
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const activeRunId = useRunStreamStore((store) => store.runId);
  const runStatus = useRunStreamStore((store) => store.status);
  const currentRunResult = useRunStreamStore((store) => store.runResult);
  const setRunId = useRunStreamStore((store) => store.setRunId);

  const visibleItems = items
    .filter((item) => ACTIONABLE_STATUSES.includes(item.status))
    .sort((a, b) => a.position - b.position);
  const queuedItems = visibleItems.filter((item) => item.status === 'queued');
  const hasQueuedItems = queuedItems.length > 0;
  const hasActiveItem = visibleItems.some(
    (item) => item.status === 'running' || item.status === 'retrying'
  );
  const nextActiveItem =
    visibleItems.find(
      (item) =>
        (item.status === 'running' || item.status === 'retrying') && item.activeRunId !== null
    ) ?? null;
  const nextActiveRunId = nextActiveItem?.activeRunId ?? null;
  const nextActiveBriefMode = nextActiveItem?.brief.mode ?? null;
  const nextActiveParentPremiseRunId =
    nextActiveItem?.brief.mode === 'scratch_ad_draft'
      ? nextActiveItem.brief.parentPremiseRunId
      : null;

  useEffect(() => {
    if (!nextActiveRunId) return;
    if (nextActiveRunId === activeRunId) return;
    if (runStatus === 'streaming') return;
    const isChildDraftOfVisiblePremise =
      currentRunResult?.mode === 'scratch_premise_set' &&
      nextActiveBriefMode === 'scratch_ad_draft' &&
      nextActiveParentPremiseRunId === currentRunResult.id;
    if (isChildDraftOfVisiblePremise) return;

    setRunId(nextActiveRunId);
  }, [
    activeRunId,
    currentRunResult?.id,
    currentRunResult?.mode,
    nextActiveBriefMode,
    nextActiveRunId,
    nextActiveParentPremiseRunId,
    runStatus,
    setRunId,
  ]);

  function moveQueuedItem(itemId: string, direction: 'up' | 'down') {
    const ids = queuedItems.map((item) => item.id);
    const index = ids.indexOf(itemId);
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || targetIndex < 0 || targetIndex >= ids.length) return;

    const nextIds = [...ids];
    const [moved] = nextIds.splice(index, 1);
    nextIds.splice(targetIndex, 0, moved as string);
    void reorder(nextIds);
  }

  function confirmAndCancelItem(itemId: string) {
    if (!window.confirm('Cancel this queued ad?')) return;
    void cancel(itemId);
  }

  async function saveEditor(body: CreateGenerationQueueItemRequest) {
    if (!editorState) return;

    if (editorState.mode === 'edit') {
      await edit(editorState.item.id, body);
    } else {
      await requeue(editorState.item.id, body);
    }

    setEditorState(null);
  }

  return (
    <Stack gap="sm" data-testid="generation-queue-panel">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <SectionHeading mb={0}>Queue</SectionHeading>
          {visibleItems.length > 0 && (
            <Badge variant="light" color={state?.status === 'paused' ? 'red' : 'gray'}>
              {visibleItems.length}
            </Badge>
          )}
        </Group>

        <Group gap={4} wrap="nowrap">
          {hasQueuedItems && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconX size={14} />}
              onClick={() => void cancelRemaining()}
              loading={mutating}
              aria-label="Cancel remaining queued ads"
            >
              Cancel remaining
            </Button>
          )}
          <Tooltip label="Refresh queue" withArrow>
            <ActionIcon
              variant="default"
              size="md"
              onClick={() => void refresh()}
              loading={loading}
              aria-label="Refresh queue"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {hasQueuedItems && hasActiveItem && (
        <Text c="dimmed" fz="xs">
          Current item will finish.
        </Text>
      )}

      {state?.status === 'paused' && (
        <Alert color="red" variant="light" title="Queue paused" data-testid="queue-paused-alert">
          <Stack gap="xs">
            <Text fz="sm">
              {state.pauseReason ?? state.pauseReasonCategory ?? 'Queue processing is paused.'}
            </Text>
            <Button
              size="xs"
              variant="filled"
              color="red"
              leftSection={<IconPlayerPlay size={14} />}
              onClick={() => void resume()}
              loading={mutating}
              aria-label="Resume queue"
            >
              Resume
            </Button>
          </Stack>
        </Alert>
      )}

      {error && (
        <Alert color="red" variant="light" data-testid="generation-queue-error">
          {error}
        </Alert>
      )}

      {visibleItems.length === 0 ? (
        <Text c="dimmed" fz="sm">
          No queued ads.
        </Text>
      ) : (
        <Stack gap="xs">
          {visibleItems.map((item) => (
            <QueueItemRow
              key={item.id}
              item={item}
              queuedItems={queuedItems}
              mutating={mutating}
              onEditItem={(itemToEdit, mode) => setEditorState({ item: itemToEdit, mode })}
              onCancel={confirmAndCancelItem}
              onMove={moveQueuedItem}
              onRequeue={requeue}
            />
          ))}
        </Stack>
      )}

      {editorState ? (
        <GenerationQueueItemEditor
          opened
          mode={editorState.mode}
          item={editorState.item}
          saving={mutating}
          onClose={() => setEditorState(null)}
          onSave={saveEditor}
        />
      ) : null}
    </Stack>
  );
}

interface QueueItemRowProps {
  item: GenerationQueueItemResponse;
  queuedItems: GenerationQueueItemResponse[];
  mutating: boolean;
  onEditItem: (item: GenerationQueueItemResponse, mode: 'edit' | 'requeue') => void;
  onCancel: (id: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onRequeue: (
    id: string,
    body: CreateGenerationQueueItemRequest
  ) => Promise<GenerationQueueItemResponse | unknown>;
}

function QueueItemRow({
  item,
  queuedItems,
  mutating,
  onEditItem,
  onCancel,
  onMove,
  onRequeue,
}: QueueItemRowProps) {
  const status = statusBadge(item.status);
  const queuedIndex = queuedItems.findIndex((queued) => queued.id === item.id);
  const canMoveUp = item.status === 'queued' && queuedIndex > 0;
  const canMoveDown =
    item.status === 'queued' && queuedIndex >= 0 && queuedIndex < queuedItems.length - 1;

  return (
    <Box
      data-testid="generation-queue-item"
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--mantine-radius-sm)',
        border: '1px solid var(--mantine-color-default-border)',
        background: 'var(--mantine-color-body)',
      }}
    >
      <Stack gap={6}>
        <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text fw={700} fz="sm" lineClamp={1}>
                {fieldLabel(item.brief.angle)}
              </Text>
              <Badge color="gray" variant="light" size="sm">
                {modeLabel(item.brief)}
              </Badge>
              <Badge color="accent" variant="light" size="sm">
                {generationModelLabel(item.brief.generationModelKey)}
              </Badge>
              <Badge color={status.color} variant="light" size="sm">
                {status.label}
              </Badge>
            </Group>
            <Text fz="sm" lineClamp={1}>
              {fieldLabel(item.brief.avatar)}
            </Text>
            <Group gap={6} wrap="wrap">
              <Text fz="xs" c="dimmed">
                Position {item.position + 1}
              </Text>
              <Text fz="xs" c="dimmed">
                Attempt {Math.min(item.attempts + 1, item.maxAttempts)}/{item.maxAttempts}
              </Text>
            </Group>
          </Stack>

          {item.status === 'queued' && (
            <Group gap={4} wrap="nowrap">
              <Tooltip label="Open editor" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => onEditItem(item, 'edit')}
                  aria-label="Edit queued ad"
                >
                  <IconEdit size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Move up" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => onMove(item.id, 'up')}
                  disabled={!canMoveUp}
                  aria-label="Move queued ad up"
                >
                  <IconArrowUp size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Move down" withArrow>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => onMove(item.id, 'down')}
                  disabled={!canMoveDown}
                  aria-label="Move queued ad down"
                >
                  <IconArrowDown size={15} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Cancel queued ad" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  loading={mutating}
                  onClick={() => onCancel(item.id)}
                  aria-label="Cancel queued ad"
                >
                  <IconX size={15} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}
        </Group>

        {item.status === 'failed' && (
          <Stack gap={6}>
            <Group gap="xs">
              {item.lastErrorCategory && (
                <Badge color="red" variant="outline" size="sm">
                  {item.lastErrorCategory}
                </Badge>
              )}
              <Text fz="sm" c="red">
                {item.lastError ?? 'This queued ad failed after all retry attempts.'}
              </Text>
            </Group>
            <Group gap="xs">
              <Button
                size="xs"
                variant="default"
                leftSection={<IconEdit size={14} />}
                onClick={() => onEditItem(item, 'requeue')}
                aria-label="Edit failed ad"
              >
                Edit
              </Button>
              <Button
                size="xs"
                variant="light"
                color="accent"
                leftSection={<IconRotateClockwise size={14} />}
                onClick={() => void onRequeue(item.id, { brief: item.brief })}
                loading={mutating}
                aria-label="Requeue failed ad"
              >
                Requeue
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

function statusBadge(status: QueueStatus): { label: string; color: string } {
  switch (status) {
    case 'queued':
      return { label: 'Queued', color: 'gray' };
    case 'running':
    case 'retrying':
      return { label: 'In progress', color: 'blue' };
    case 'completed':
      return { label: 'Done', color: 'green' };
    case 'failed':
      return { label: 'Failed', color: 'red' };
    case 'cancelled':
      return { label: 'Cancelled', color: 'gray' };
  }
}

function modeLabel(brief: GenerationQueueItemResponse['brief']): string {
  switch (brief.mode) {
    case 'scratch_premise_set':
      return 'Premise set';
    case 'scratch_ad_draft':
      return 'Scratch draft';
    case 'fb_ad_to_advertorial':
      return 'Facebook advertorial';
    case 'competitor_advertorial_to_advertorial':
      return 'Competitor advertorial';
    default:
      return 'Existing structure';
  }
}

function fieldLabel(field: EntryOrFreeText): string {
  if ('ephemeralValue' in field) return field.ephemeralValue;
  if ('value' in field) return field.value;
  return `Entry ${field.entryId}`;
}
